// ============================================================================
// WeeklyGoalForm — the trainer sets THIS WEEK's goal for one client (Wave 3).
// ----------------------------------------------------------------------------
// The trainer's plan for the current week (the Monday `week_start`):
//   • kcal / protein / carbs / fat / water targets (any may be left blank to use
//     the auto-recommended value — a per-field coach override),
//   • a small editable list of exercise homework targets ({type, count}, type
//     from the session-type buckets strength|cardio|mobility|hiit|mixed),
//   • free-text notes.
// PUT /api/clients/{id}/weekly-goal (upsert, idempotent per client+week). When a
// goal is set, its nutrition targets OVERRIDE the auto daily targets for that
// week, so the trainer's ClientNutritionPanel + the client's NutritionTargetsCard
// flip `source` to 'coach' ("Set by your coach") — backend-driven; no FE work
// needed beyond saving here.
//
// Shows the current week's goal as a read-only summary when set (Edit reveals the
// form); otherwise opens straight to the form. Mobile-first, Pulse, EN + 繁中,
// reduced-motion aware (no bespoke animation). All strings via the weeklyGoals
// i18n namespace — NEVER renders server-provided text.
// ============================================================================
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Badge, Banner, Button, Card, CardHeader, SelectField, TextField } from "../../components";
import { LoadingBlock } from "../../app/PageState";
import { useClientWeeklyGoal, useUpsertWeeklyGoal } from "../../hooks/weeklyGoals";
import type { ExerciseTarget, WeeklyGoal, WeeklyGoalInput } from "../../api";
import { formatDayLong } from "../../lib/date";
import { fmtNum } from "./lib";

/** The session-type buckets a homework target can use (matches what a client logs under). */
const EXERCISE_TYPES = ["strength", "cardio", "mobility", "hiit", "mixed"] as const;

/** "" / non-numeric -> undefined; else the number (so a blank clears to auto). */
function numOrUndef(s: string): number | undefined {
  const v = s.trim();
  if (v === "") return undefined;
  const n = Number(v);
  return Number.isNaN(n) ? undefined : n;
}

/** Render a stored target (number | null) as a display string, or the blank dash. */
function showTarget(v: number | null, blank: string): string {
  return v == null ? blank : fmtNum(v, 0);
}

/** A draft exercise-target row in the editable list (string count for the input). */
interface TargetRow {
  type: string;
  count: string;
}

function rowsFromGoal(goal: WeeklyGoal | null): TargetRow[] {
  if (!goal || goal.exercise_targets.length === 0) return [];
  return goal.exercise_targets.map((tg) => ({
    type: tg.type ?? "",
    count: tg.count != null ? String(tg.count) : "",
  }));
}

// --- read-only summary of the saved goal -----------------------------------

function GoalSummary({ goal }: { goal: WeeklyGoal }) {
  const { t } = useTranslation("weeklyGoals");
  const blank = t("trainer.blank");
  const rows: { label: string; value: string; unit?: string }[] = [
    { label: t("targets.kcal"), value: showTarget(goal.kcal_target, blank), unit: t("targets.kcalUnit") },
    { label: t("targets.protein"), value: showTarget(goal.protein_target_g, blank), unit: t("targets.gUnit") },
    { label: t("targets.carbs"), value: showTarget(goal.carbs_target_g, blank), unit: t("targets.gUnit") },
    { label: t("targets.fat"), value: showTarget(goal.fat_target_g, blank), unit: t("targets.gUnit") },
    { label: t("targets.water"), value: showTarget(goal.water_target_ml, blank), unit: t("targets.mlUnit") },
  ];

  return (
    <div className="space-y-5">
      <div>
        <p className="mb-2 font-display text-xs font-bold uppercase tracking-kicker text-body-soft">
          {t("trainer.summaryTitle")}
        </p>
        <dl className="grid grid-cols-2 gap-2 sm:grid-cols-3">
          {rows.map((r) => (
            <div key={r.label} className="rounded-md bg-surface-sunken px-3 py-2.5">
              <dt className="text-xs font-bold text-body-soft">{r.label}</dt>
              <dd className="mt-0.5 font-display text-base font-extrabold tabular-nums text-body">
                {r.value}
                {r.value !== blank && r.unit && (
                  <span className="ml-1 text-xs font-bold text-body-soft">{r.unit}</span>
                )}
              </dd>
            </div>
          ))}
        </dl>
      </div>

      <div>
        <p className="mb-2 font-display text-xs font-bold uppercase tracking-kicker text-body-soft">
          {t("trainer.exerciseTargets")}
        </p>
        {goal.exercise_targets.length === 0 ? (
          <p className="text-sm text-body-soft">{t("trainer.noExerciseTargets")}</p>
        ) : (
          <ul className="flex flex-wrap gap-2">
            {goal.exercise_targets.map((tg, i) => (
              <li key={`${tg.type}-${i}`}>
                <Badge tone="info">
                  {t(`exercise.type.${tg.type}`, tg.type)} · {fmtNum(tg.count ?? 0, 0)}
                </Badge>
              </li>
            ))}
          </ul>
        )}
      </div>

      {goal.notes && goal.notes.trim() && (
        <div>
          <p className="mb-1 font-display text-xs font-bold uppercase tracking-kicker text-body-soft">
            {t("notesLabel")}
          </p>
          <p className="whitespace-pre-wrap text-sm text-body">{goal.notes}</p>
        </div>
      )}
    </div>
  );
}

// --- the editable form ------------------------------------------------------

function GoalEditor({
  clientId,
  goal,
  onSaved,
  onCancel,
}: {
  clientId: number;
  goal: WeeklyGoal | null;
  onSaved: () => void;
  onCancel?: () => void;
}) {
  const { t } = useTranslation("weeklyGoals");
  const upsert = useUpsertWeeklyGoal();

  const [kcal, setKcal] = useState(() => (goal?.kcal_target != null ? String(goal.kcal_target) : ""));
  const [protein, setProtein] = useState(() =>
    goal?.protein_target_g != null ? String(goal.protein_target_g) : "",
  );
  const [carbs, setCarbs] = useState(() => (goal?.carbs_target_g != null ? String(goal.carbs_target_g) : ""));
  const [fat, setFat] = useState(() => (goal?.fat_target_g != null ? String(goal.fat_target_g) : ""));
  const [water, setWater] = useState(() =>
    goal?.water_target_ml != null ? String(goal.water_target_ml) : "",
  );
  const [rows, setRows] = useState<TargetRow[]>(() => rowsFromGoal(goal));
  const [notes, setNotes] = useState(() => goal?.notes ?? "");

  const typeOptions = EXERCISE_TYPES.map((tp) => ({
    value: tp,
    label: t(`exercise.type.${tp}`),
  }));

  const addRow = () => setRows((rs) => [...rs, { type: "strength", count: "" }]);
  const removeRow = (idx: number) => setRows((rs) => rs.filter((_, i) => i !== idx));
  const setRow = (idx: number, patch: Partial<TargetRow>) =>
    setRows((rs) => rs.map((r, i) => (i === idx ? { ...r, ...patch } : r)));

  const submit = () => {
    // Build a full body: every target sent explicitly (a blank clears to null so
    // the per-field coach override falls back to the auto value).
    const exerciseTargets: ExerciseTarget[] = rows
      .map((r) => ({ type: r.type.trim(), count: numOrUndef(r.count) }))
      .filter((r): r is { type: string; count: number } => r.type !== "" && r.count !== undefined && r.count > 0)
      .map((r) => ({ type: r.type, count: r.count }));

    const body: WeeklyGoalInput = {
      kcal_target: numOrUndef(kcal) ?? null,
      protein_target_g: numOrUndef(protein) ?? null,
      carbs_target_g: numOrUndef(carbs) ?? null,
      fat_target_g: numOrUndef(fat) ?? null,
      water_target_ml: numOrUndef(water) ?? null,
      exercise_targets: exerciseTargets,
      notes: notes.trim() || null,
    };

    upsert.mutate(
      { clientId, body },
      { onSuccess: () => onSaved() },
    );
  };

  return (
    <div className="space-y-5">
      <Banner tone="info" title={t("trainer.leaveBlank")} />

      {/* Macro + water targets */}
      <div className="grid grid-cols-2 gap-4">
        <TextField
          label={t("trainer.kcalTarget")}
          value={kcal}
          onChange={(e) => setKcal(e.target.value)}
          type="number"
          inputMode="numeric"
          min={0}
          suffix={t("targets.kcalUnit")}
        />
        <TextField
          label={t("trainer.waterTarget")}
          value={water}
          onChange={(e) => setWater(e.target.value)}
          type="number"
          inputMode="numeric"
          min={0}
          suffix={t("targets.mlUnit")}
        />
        <TextField
          label={t("trainer.proteinTarget")}
          value={protein}
          onChange={(e) => setProtein(e.target.value)}
          type="number"
          inputMode="numeric"
          min={0}
          suffix={t("targets.gUnit")}
        />
        <TextField
          label={t("trainer.carbsTarget")}
          value={carbs}
          onChange={(e) => setCarbs(e.target.value)}
          type="number"
          inputMode="numeric"
          min={0}
          suffix={t("targets.gUnit")}
        />
        <TextField
          label={t("trainer.fatTarget")}
          value={fat}
          onChange={(e) => setFat(e.target.value)}
          type="number"
          inputMode="numeric"
          min={0}
          suffix={t("targets.gUnit")}
        />
      </div>

      {/* Exercise homework targets — editable list of {type, count} */}
      <div>
        <p className="mb-2 font-display text-sm font-bold text-body">
          {t("trainer.exerciseTargets")}
        </p>
        {rows.length === 0 ? (
          <p className="mb-2 text-sm text-body-soft">{t("trainer.noExerciseTargets")}</p>
        ) : (
          <ul className="space-y-2">
            {rows.map((row, idx) => (
              <li key={idx} className="flex items-end gap-2">
                <div className="flex-1">
                  <SelectField
                    label={t("trainer.exerciseType")}
                    hideLabel
                    options={typeOptions}
                    value={row.type}
                    onChange={(e) => setRow(idx, { type: e.target.value })}
                  />
                </div>
                <div className="w-28">
                  <TextField
                    label={t("trainer.exerciseCount")}
                    hideLabel
                    placeholder={t("trainer.exerciseCount")}
                    value={row.count}
                    onChange={(e) => setRow(idx, { count: e.target.value })}
                    type="number"
                    inputMode="numeric"
                    min={1}
                  />
                </div>
                <button
                  type="button"
                  onClick={() => removeRow(idx)}
                  aria-label={t("trainer.removeAria")}
                  className="grid h-12 w-10 shrink-0 place-items-center rounded-md text-body-muted transition-colors hover:bg-surface-sunken hover:text-coral focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-volt"
                >
                  <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" aria-hidden>
                    <path
                      d="M6 6l12 12M18 6 6 18"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                  </svg>
                </button>
              </li>
            ))}
          </ul>
        )}
        <Button variant="ghost" size="sm" className="mt-2" onClick={addRow}>
          {t("trainer.addExerciseTarget")}
        </Button>
      </div>

      {/* Notes */}
      <div className="w-full">
        <label
          htmlFor="vf-wgoal-notes"
          className="mb-1.5 block font-display text-sm font-bold text-body"
        >
          {t("trainer.notes")}
        </label>
        <textarea
          id="vf-wgoal-notes"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={2}
          placeholder={t("trainer.notesPlaceholder")}
          className="w-full resize-y rounded-md bg-surface px-4 py-3 text-base text-body placeholder:text-body-faint ring-1 ring-inset ring-line-strong shadow-xs transition-shadow duration-fast focus:ring-2 focus:ring-inset focus:ring-volt focus-visible:outline-none"
        />
      </div>

      {upsert.isError && <Banner tone="danger" title={t("trainer.saveError")} />}

      <div className="flex items-center gap-3">
        {onCancel && (
          <Button variant="ghost" onClick={onCancel} disabled={upsert.isPending}>
            {t("trainer.cancel")}
          </Button>
        )}
        <div className="flex-1" />
        <Button onClick={submit} disabled={upsert.isPending}>
          {upsert.isPending ? t("trainer.saving") : t("trainer.save")}
        </Button>
      </div>
    </div>
  );
}

// --- panel ------------------------------------------------------------------

export function WeeklyGoalForm({ clientId }: { clientId: number }) {
  const { t } = useTranslation("weeklyGoals");
  const query = useClientWeeklyGoal(clientId);
  const [editing, setEditing] = useState(false);

  // When the saved goal arrives (or is cleared), default to summary view if one
  // exists, editor if none. Re-evaluated only when the saved goal id changes.
  const goal = query.data?.goal ?? null;
  useEffect(() => {
    setEditing(goal == null);
  }, [goal?.id, goal == null]);

  const weekStart = query.data?.week_start;
  const weekLabel = weekStart ? t("trainer.currentWeek", { date: formatDayLong(`${weekStart}T00:00`) }) : "";

  if (query.isLoading) {
    return (
      <Card>
        <CardHeader title={t("trainer.title")} subtitle={t("trainer.subtitle")} />
        <LoadingBlock />
      </Card>
    );
  }
  if (query.isError || !query.data) {
    return (
      <Card>
        <CardHeader title={t("trainer.title")} subtitle={t("trainer.subtitle")} />
        <Banner tone="warning" title={t("trainer.loadError")} />
      </Card>
    );
  }

  return (
    <Card className="space-y-5">
      <div className="flex items-start justify-between gap-3">
        <CardHeader title={t("trainer.title")} subtitle={t("trainer.subtitle")} />
        {!editing && goal && (
          <Button variant="secondary" size="sm" onClick={() => setEditing(true)}>
            {t("trainer.editGoal")}
          </Button>
        )}
      </div>

      {weekLabel && (
        <p className="text-sm font-bold text-body-soft">{weekLabel}</p>
      )}

      {editing ? (
        <GoalEditor
          clientId={clientId}
          goal={goal}
          onSaved={() => setEditing(false)}
          onCancel={goal ? () => setEditing(false) : undefined}
        />
      ) : goal ? (
        <GoalSummary goal={goal} />
      ) : (
        <div className="space-y-3">
          <Banner tone="info" title={t("trainer.noGoalYet")} />
          <Button onClick={() => setEditing(true)}>{t("trainer.setGoal")}</Button>
        </div>
      )}
    </Card>
  );
}
